import {
	DowncastConversionApi,
	EventInfo,
	ModelElement,
	ModelRange,
	Plugin,
	ViewDowncastWriter,
	ViewElement,
} from 'ckeditor5';

declare module '@ckeditor/ckeditor5-core' {
	// eslint-disable-next-line no-unused-vars
	interface PluginsMap {
		[ImageBlockInlineStyle.pluginName]: ImageBlockInlineStyle;
	}
}

/**
 * This plugin ensures image blocks render correctly in email clients
 * by applying all necessary styles as inline styles, instead of relying on CSS classes.
 */
class ImageBlockInlineStyle extends Plugin {
	public static get pluginName() {
		return 'EmailStyle' as const;
	}

	public init(): void {
		const { editor } = this;

		/**
		 * Overrides the default upcast behaviour to preserve the figure's original width style,
		 * preventing CKEditor from overwriting it with the image's CSS width.
		 *
		 * By default, CKEditor takes the image's width and applies it to the figure during upcast.
		 * This plugin stops that, keeping the figure's own width untouched.
		 */
		editor.conversion.for('upcast').attributeToAttribute({
			view: { name: 'img', styles: { width: /.+/ } },
			model: {
				key: 'imgWidth',
				value: (view: ViewElement) => this.keepFigureWidth(view),
			},
		});

		/**
		 * Ensures the image adapts properly to the figure's width when wrapped in HTML tags.
		 *
		 * In CKEditor, images are wrapped inside a `<figure>` tag with an <img> tag inside.
		 * If the image was resized in the editor, the `<figure>` will have an inline width style.
		 * In that case, we set the `<img>` width to `-webkit-fill-available` so it fills the `<figure>`'s width.
		 * If the <figure> doesn't have a width, we set the `<img>`'s max-width to 100% to prevent overflow.
		 */
		editor.conversion.for('downcast').add((dispatcher) => {
			dispatcher.on('insert:imageBlock', this.handleInsertImageBlock.bind(this), { priority: 'low' });
		});
	}

	private handleInsertImageBlock(
		evt: EventInfo,
		data: { item: ModelElement; range: ModelRange },
		api: DowncastConversionApi
	): void {
		const figure = api.mapper.toViewElement(data.item);
		if (figure) this.applyInlineStylesToFigureAndImage(figure, api.writer);
	}

	private applyInlineStylesToFigureAndImage(figure: ViewElement, writer: ViewDowncastWriter): void {
		this.applyFigureStyles(figure, writer);
		this.applyImageStylesInsideFigure(figure, writer);
	}

	private applyFigureStyles(figure: ViewElement, writer: ViewDowncastWriter): void {
		const currentStyle = figure.getAttribute('style') || '';

		if (!styleIncludesProperty(currentStyle, 'margin'))
			writer.setAttribute('style', mergeStyles(currentStyle, 'margin: 0.9em auto 0.9em 0; max-width: 100%;'), figure);
	}

	private applyImageStylesInsideFigure(figure: ViewElement, writer: ViewDowncastWriter): void {
		const figureHasWidth = styleIncludesProperty(figure.getAttribute('style') || '', 'width');
		const styles = ['display: block', 'max-width: 100%', 'height: auto'];

		if (figureHasWidth) styles.push('width: -webkit-fill-available;');
		else styles.push('max-width: 100%;');

		for (const child of figure.getChildren()) {
			if (child.is('element') && child.name === 'img') this.applyStyles(child, writer, styles);
		}
	}

	private applyStyles(element: ViewElement, writer: ViewDowncastWriter, styles: string[]): void {
		const current = element.getAttribute('style') || '';
		const missing = styles.filter((style) => !current.includes(style));
		if (missing.length) writer.setAttribute('style', mergeStyles(current, missing.join('; ')), element);
	}

	private keepFigureWidth(view: ViewElement): ViewElement {
		if (!view?.parent?.is('element', 'figure')) return view;

		const width = view.parent.getStyle('width');
		if (width) view._setAttribute('style', `width: ${width}`, true);

		return view;
	}
}

function styleIncludesProperty(style: string, prop: string): boolean {
	return style
		.split(';')
		.map((style) => style.trim().split(':')[0].toLowerCase())
		.includes(prop.toLowerCase());
}

function mergeStyles(existing: string, addition: string): string {
	const trimmed = existing.trim();
	return trimmed ? `${trimmed}; ${addition}` : addition;
}

export default ImageBlockInlineStyle;
